// @djsem/analysis-contract — Event & Release Value-Add Layer
// Prompt 43: Event Brief Builder
// Prompt 45: Technical Rider / Venue Handoff
// Prompt 46: Missing Track Finder / Playlist Gap Analysis
// Prompt 47: EPK / Press Kit Generator
// Prompt 52: Brand Consistency Scoring
// Prompt 53: Release Readiness Coach

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult } from "@djsem/shared";
import { SeqCounter } from "@djsem/shared";
const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();

// ============================================================================
// PROMPT 43 — Event Brief Builder
// ============================================================================

/** The kind of event a brief is being built for; drives recommended deliverables and risk checks. */
export type DJSEMEventType =
  | "wedding" | "private_party" | "bar" | "lounge" | "small_club"
  | "festival_slot" | "corporate_event" | "brand_activation" | "release_party"
  | "warmup_slot" | "headline_slot" | "livestream" | "custom";

/** The expected energy level of an event's audience. */
export type DJSEMEventAudienceEnergy =
  | "background" | "warmup" | "steady_groove" | "dancefloor_build"
  | "peak_time" | "mixed_energy" | "custom";

/** A single must-play or do-not-play track requirement requested for an event. */
export interface DJSEMEventTrackRequirement {
  id: string;
  title?: string; artist?: string;
  reason?: string;
  status: "unresolved" | "matched" | "missing" | "rejected";
  matchedTrackId?: string;
  warnings: string[];
}

/** A single target point along an event's planned energy arc. */
export interface DJSEMEventEnergyArcPoint {
  label: "arrival" | "warmup" | "build" | "peak" | "cooldown" | "special_moment" | "custom";
  relativeTimePercent: number;
  targetEnergy: number;
  notes?: string;
}

/** Expected audience characteristics and content policy for an event. */
export interface DJSEMAudienceProfile {
  expectedEnergy: DJSEMEventAudienceEnergy;
  explicitLyricsAllowed: boolean;
  familyFriendlyRequired: boolean;
  warnings: string[];
}

/** The full set of music constraints and preferences requested for an event. */
export interface DJSEMMusicRequirementSet {
  genresPreferred: string[];
  genresAvoid: string[];
  mustPlayTrackRefs: DJSEMEventTrackRequirement[];
  doNotPlayTrackRefs: DJSEMEventTrackRequirement[];
  cleanOnly: boolean;
  bpmRange?: { min?: number; max?: number };
  energyArc: DJSEMEventEnergyArcPoint[];
  warnings: string[];
}

/** A single deliverable (set, crate, technical rider, etc.) required or recommended for an event. */
export interface DJSEMEventDeliverableRequirement {
  id: string;
  deliverableType: string;
  required: boolean;
  status: "not_started" | "in_progress" | "ready" | "approved" | "skipped";
  warnings: string[];
}

/** The consolidated brief for a single event/gig: audience, music requirements, and deliverables. */
export interface DJSEMEventBrief {
  id: string;
  projectId: string;
  title: string;
  eventType: DJSEMEventType;
  status: "draft" | "review_requested" | "approved" | "archived";
  eventDate?: string;
  venueName?: string;
  performanceDurationMinutes?: number;
  audienceProfile: DJSEMAudienceProfile;
  musicRequirements: DJSEMMusicRequirementSet;
  deliverables: DJSEMEventDeliverableRequirement[];
  notes: string[];
  warnings: string[];
  createdBy?: string;
  createdAt: string;
  updatedAt: string;
}

/** A single detected risk or gap in an event brief, with a suggested fix. */
export interface DJSEMEventBriefRisk {
  id: string;
  severity: "info" | "warning" | "high" | "critical";
  riskType: string;
  message: string;
  suggestedFix: string;
}

/** Input required to create a new {@link DJSEMEventBrief}. */
export interface CreateEventBriefInput extends Omit<DJSEMEventBrief, "id" | "status" | "createdAt" | "updatedAt" | "warnings"> {
  id?: string;
}

/** Creates, validates, and risk-checks event briefs, and recommends their deliverables. */
export class EventBriefService {
  /**
   * Creates a new draft event brief.
   * @param input - the event's type, audience profile, music requirements, and deliverables.
   * @returns the newly created brief in `"draft"` status.
   */
  createBrief(input: CreateEventBriefInput): DJSEMEventBrief {
    return { ...input, id: input.id ?? `eb_${_seq.next()}`, status: "draft", warnings: [], createdAt: AT, updatedAt: AT };
  }

  /**
   * Validates that a brief has the minimum required fields (title, project id, event type).
   * @param brief - the brief to validate.
   * @returns the validation result.
   */
  validateBrief(brief: DJSEMEventBrief): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (!brief.title.trim()) issues.push({ path: "$.title", message: "required" });
    if (!brief.projectId) issues.push({ path: "$.projectId", message: "required" });
    if (!brief.eventType) issues.push({ path: "$.eventType", message: "required" });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "event_brief");
  }

  /**
   * Marks a brief as approved.
   * @param brief - the brief to approve.
   * @returns a copy of the brief with `status: "approved"`.
   */
  approveBrief(brief: DJSEMEventBrief): DJSEMEventBrief {
    return { ...brief, status: "approved", updatedAt: AT };
  }

  /**
   * Scans a brief for common gaps and conflicts (unresolved must-plays, explicit-lyrics policy
   * conflicts, missing duration, missing client approval deliverable).
   * @param brief - the brief to check.
   * @returns a list of detected risks, each with a suggested fix.
   */
  detectRisks(brief: DJSEMEventBrief): DJSEMEventBriefRisk[] {
    const risks: DJSEMEventBriefRisk[] = [];
    const unresolved = brief.musicRequirements.mustPlayTrackRefs.filter(t => t.status === "unresolved");
    if (unresolved.length > 0) risks.push({ id: `r_${_seq.next()}`, severity: "high", riskType: "missing_must_play", message: `${unresolved.length} must-play track(s) unresolved`, suggestedFix: "Resolve must-play tracks in library" });
    if (brief.musicRequirements.cleanOnly && brief.audienceProfile.explicitLyricsAllowed) risks.push({ id: `r_${_seq.next()}`, severity: "warning", riskType: "explicit_lyrics_conflict", message: "cleanOnly is set but explicit lyrics are marked allowed", suggestedFix: "Clarify explicit policy with client" });
    if (!brief.performanceDurationMinutes) risks.push({ id: `r_${_seq.next()}`, severity: "warning", riskType: "duration_unknown", message: "Performance duration not set", suggestedFix: "Set expected duration" });
    if (brief.eventType === "wedding" || brief.eventType === "private_party" || brief.eventType === "corporate_event") {
      if (!brief.deliverables.some(d => d.deliverableType === "client_preview")) risks.push({ id: `r_${_seq.next()}`, severity: "info", riskType: "approval_missing", message: "Client approval recommended for this event type", suggestedFix: "Add client preview deliverable" });
    }
    return risks;
  }

  /**
   * Recommends a default set of deliverables based on the event type (set, crate, and
   * type-specific extras like client preview or technical rider).
   * @param brief - the brief whose event type drives the recommendations.
   * @returns a list of recommended deliverable requirements.
   */
  recommendDeliverables(brief: DJSEMEventBrief): DJSEMEventDeliverableRequirement[] {
    const deliverables: DJSEMEventDeliverableRequirement[] = [
      { id: `del_${_seq.next()}`, deliverableType: "djsem_set", required: true, status: "not_started", warnings: [] },
      { id: `del_${_seq.next()}`, deliverableType: "smart_crate", required: true, status: "not_started", warnings: [] },
    ];
    if (["wedding", "private_party", "corporate_event", "brand_activation"].includes(brief.eventType)) {
      deliverables.push({ id: `del_${_seq.next()}`, deliverableType: "client_preview", required: true, status: "not_started", warnings: [] });
    }
    if (["small_club", "festival_slot", "headline_slot"].includes(brief.eventType)) {
      deliverables.push({ id: `del_${_seq.next()}`, deliverableType: "technical_rider", required: true, status: "not_started", warnings: [] });
    }
    deliverables.push({ id: `del_${_seq.next()}`, deliverableType: "gig_pack", required: false, status: "not_started", warnings: [] });
    return deliverables;
  }
}
